import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart, Star, Search, MapPin, Trash2, Bike, Timer, Heart } from "lucide-react";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../components/ui/sheet";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { useToast } from "../hooks/use-toast";
import { CITY, DIETS, SUBSCRIPTIONS, CUSTOMIZATION_OPTIONS } from "../mock";
import { api, setAuthToken, favVendor, unfavVendor, authMe, mySubscriptions } from "../lib/api";

// ---------------------- Auth Context ----------------------
const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("prepmate_token") || "");
  const [user, setUser] = useState(() => {
    try { const raw = localStorage.getItem("prepmate_user"); return raw ? JSON.parse(raw) : null; } catch { return null; }
  });
  
  // Build-time env var fallbacks — set REACT_APP_PILOT_MODE=true in Vercel env vars
  const ENV_PILOT = process.env.REACT_APP_PILOT_MODE === 'true';
  const ENV_PILOT_VENDOR_ID = process.env.REACT_APP_PILOT_VENDOR_ID || null;

  // Config loading state — skip if build-time env var is already set
  const [configLoading, setConfigLoading] = useState(!ENV_PILOT);
  const [isPilot, setIsPilot] = useState(ENV_PILOT);
  const [pilotVendorId, setPilotVendorId] = useState(ENV_PILOT_VENDOR_ID);

  // Auth error tracking for diagnostics
  const [lastAuthError, setLastAuthError] = useState(null);

  // Fetch runtime config on mount — overrides build-time env vars if available
  useEffect(() => {
    (async () => {
      try {
        const { data: cfg } = await api.get('/config');
        // Only apply if we got a real JSON config object (not HTML fallback from Vercel SPA)
        if (cfg && typeof cfg === 'object' && 'pilot_mode' in cfg) {
          setIsPilot(!!cfg.pilot_mode);
          setPilotVendorId(cfg.pilot_vendor_id || null);
        }
      } catch {
        // Keep build-time env var values on error — do not reset to false
      } finally {
        setConfigLoading(false);
      }
    })();
  }, []);

  useEffect(() => { setAuthToken(token); }, [token]);
  useEffect(() => {
    localStorage.setItem("prepmate_user", JSON.stringify(user));
  }, [user]);

  // Always hydrate user + active subscription when we have a token
  useEffect(() => {
    if (!token) {
      setLastAuthError(null);
      return;
    }
    (async () => {
      try {
        setLastAuthError(null);
        const u = await authMe();
        let merged = u;
        try {
          const subs = await mySubscriptions();
          const active = subs.find(s => (s.status||'') === 'active');
          if (active) merged = { ...u, active_subscription: { company_id: active.company_id, plan_id: active.plan_id, id: active.id } };
        } catch (subErr) {
          // Subscription fetch failure is not an auth error - user is still logged in
          console.log('[Auth] Subscription fetch failed (non-fatal):', subErr?.message);
        }
        setUser(merged);
      } catch (err) {
        // Only set auth error if the /me endpoint itself fails
        setLastAuthError(err?.response?.data?.detail || err?.message || 'Profile fetch failed');
        console.error('[Auth] User hydration failed:', err?.message);
      }
    })();
  }, [token]);

  const value = useMemo(() => ({ 
    token, setToken, 
    user, setUser, 
    isPilot, pilotVendorId, configLoading,
    lastAuthError, setLastAuthError
  }), [token, user, isPilot, pilotVendorId, configLoading, lastAuthError]);
  
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------- Cart Context ----------------------
const CartContext = createContext();
export const useCart = () => useContext(CartContext);

function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { const raw = localStorage.getItem("prepmate_cart"); return raw ? JSON.parse(raw) : []; } catch { return []; }
  });
  const [subscriptionId, setSubscriptionId] = useState("sub-5");
  const [fulfillment, setFulfillment] = useState("weekly");

  useEffect(() => { localStorage.setItem("prepmate_cart", JSON.stringify(items)); }, [items]);

  const sub = SUBSCRIPTIONS.find(s => s.id === subscriptionId) || SUBSCRIPTIONS[1];
  const subtotal = items.reduce((acc, it) => acc + it.totalPrice, 0);
  const total = subtotal;

  const value = useMemo(() => ({ items, setItems, addItem:(item)=>setItems(p=>[...p,item]), removeItem:(idx)=>setItems(p=>p.filter((_,i)=>i!==idx)), clear:()=>setItems([]), subscriptionId, setSubscriptionId, sub, fulfillment, setFulfillment, subtotal, total }), [items, subscriptionId, sub, fulfillment, subtotal, total]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

// ---------------------- Navbar ----------------------
export function Navbar() {
  const { user, setUser, setToken, isPilot, pilotVendorId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { items } = useCart();

  function signOut(){
    try {
      localStorage.removeItem('prepmate_token');
      localStorage.removeItem('prepmate_user');
    } catch {}
    setToken("");
    setUser(null);
    navigate("/");
  }

  // Simplified navbar on /admin and /superadmin
  if (location.pathname === '/admin' || location.pathname === '/superadmin') {
    return (
      <div className="sticky top-0 z-40 border-b bg-[#f5efe6]/90 backdrop-blur">
        <div className="mx-auto max-w-6xl px-4 py-3 flex items-center">
          <span className="flex items-center gap-1.5 font-semibold tracking-tight text-black text-xl">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M12 2C9.5 5.5 7.5 8.5 7.5 12.5C7.5 16.6 9.5 21 12 21C14.5 21 16.5 16.6 16.5 12.5C16.5 8.5 14.5 5.5 12 2Z" fill="#2a7d4f"/>
              <path d="M12 21V12" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            PrepMate
          </span>
          <div className="flex items-center gap-3 ml-auto">
            {user && <span className="text-sm text-black/80">{user.name}</span>}
            <button onClick={signOut} className="text-sm text-black/80 hover:text-black underline">Sign out</button>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard click handler - role and pilot mode aware
  function handleDashboardClick(e) {
    e.preventDefault();
    if (user?.role === 'superadmin') {
      navigate("/superadmin");
    } else if (user?.role === 'company' || user?.role === 'vendor' || user?.role === 'admin') {
      navigate("/admin");
    } else if (isPilot && !user?.vendor_id) {
      navigate("/pilot-not-joined");
    } else {
      navigate("/dashboard");
    }
  }

  // Logo click handler - pilot mode aware
  function handleLogoClick(e) {
    e.preventDefault();
    if (user?.role === 'superadmin') {
      navigate("/superadmin");
      return;
    }
    navigate("/");
  }

  const showMarketplace = !isPilot;
  return (
    <div className="sticky top-0 z-40 border-b bg-[#f5efe6]/90 backdrop-blur">
      <div className="mx-auto max-w-6xl px-4 py-3 flex items-center gap-4">
        <a href="/" onClick={handleLogoClick} className="flex items-center gap-1.5 font-semibold tracking-tight text-black text-xl cursor-pointer">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M8 4V9.5M12 4V9.5M16 4V9.5M8 9.5C8 11.4 9.8 13 12 13C14.2 13 16 11.4 16 9.5M12 13V21" stroke="#2a7d4f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          PrepMate
        </a>
        {showMarketplace && (
          <>
            <div className="hidden md:flex items-center gap-2 px-3 py-2 rounded-md border bg-white flex-1">
              <MapPin size={16} className="text-neutral-700" />
              <span className="text-sm text-neutral-700">{user?.location || CITY.label}</span>
              <div className="h-5 w-px bg-neutral-200 mx-2" />
              <Search size={16} className="text-neutral-700" />
              <input placeholder="Search meal prep" className="flex-1 outline-none text-sm placeholder:text-neutral-400" />
            </div>
            <Button variant="outline" className="hidden md:inline-flex bg-white border-black/10 hover:bg-[#2a7d4f] hover:text-white transition-colors" onClick={() => navigate("/browse")}>Browse</Button>
            <button
              onClick={() => navigate('/browse')}
              className="md:hidden p-2 rounded-lg hover:bg-neutral-100 text-neutral-700"
              aria-label="Search vendors"
            >
              <Search size={20} />
            </button>
          </>
        )}
        {user ? (
          <div className="flex items-center gap-3 ml-auto">
            <a href="/dashboard" onClick={handleDashboardClick} className="text-sm text-black/80 hover:text-black">Dashboard</a>
            <span className="text-sm text-black/80">Hi, {user.name?.toLowerCase().includes('demo') && !isPilot ? 'User' : user.name}</span>
            <button onClick={signOut} className="text-sm text-black/80 hover:text-black underline">Sign out</button>
          </div>
        ) : (
          <Link to="/auth" className="text-sm text-black/80 hover:text-black ml-auto">Sign in</Link>
        )}
        {!isPilot && <CartButton />}
      </div>
    </div>
  );
}

function CartButton() { const { items } = useCart(); return (<Sheet><SheetTrigger asChild><Button className="relative bg-[#2a7d4f] text-white hover:bg-[#236840]"><ShoppingCart size={18} className="mr-2" />Cart{items.length>0&&(<span className="ml-2 rounded-full bg-white text-black text-xs px-2 py-0.5">{items.length}</span>)}</Button></SheetTrigger><SheetContent className="w-[420px] sm:w-[520px]"><SheetHeader><SheetTitle>Your cart</SheetTitle></SheetHeader><CartContents compact={false} /></SheetContent></Sheet>); }

// ---------------------- Company Card ----------------------
export function CompanyCard({ vendor }) {
  const { user, setUser } = useAuth();
  const isFav = !!user?.favorite_vendors?.includes(vendor.id);
  async function toggleFav(e){ e.preventDefault(); if(!user){ return; } try { if(isFav){ await unfavVendor(vendor.id); setUser({ ...user, favorite_vendors: (user.favorite_vendors||[]).filter(id=>id!==vendor.id) }); } else { await favVendor(vendor.id); setUser({ ...user, favorite_vendors: [...(user.favorite_vendors||[]), vendor.id] }); } } catch{}
  }
  return (
    <Link to={`/company/${vendor.id}`} className="group block rounded-2xl overflow-hidden border bg-white hover:shadow-md transition-shadow">
      <div className="relative aspect-[16/10] overflow-hidden">
        <img loading="lazy" sizes="(max-width: 768px) 100vw, 33vw" src={vendor.image} alt={vendor.name} className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
        <div className="absolute top-2 left-2 flex gap-2">{vendor.diets?.slice(0,2).map(d => (<Badge key={d} variant="secondary" className="bg-white/90 border border-neutral-200 text-xs">{d}</Badge>))}</div>
        {user && (
          <button onClick={toggleFav} className="absolute top-2 right-2 rounded-full bg-white/90 p-1 border hover:bg-white">
            <Heart size={16} className={isFav?"text-red-500 fill-red-500":"text-neutral-700"} />
          </button>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-center justify-between"><h3 className="font-semibold text-black">{vendor.name}</h3><span className="text-xs text-neutral-500">{vendor.eta}</span></div>
        <div className="mt-1 text-xs text-neutral-600">{(vendor.cuisines||[]).join(", ")}</div>
        <div className="mt-2 flex items-center gap-2 text-sm"><span className="inline-flex items-center gap-1"><Star size={14} className="text-amber-500" /> {(vendor.rating||0).toFixed(1)}</span><span className="text-neutral-400">•</span><span className="text-neutral-600">{vendor.reviews} reviews</span></div>
      </div>
    </Link>
  );
}

// ---------------------- Filter bar ----------------------
export function FilterBar({ diet, setDiet, sort, setSort }) {
  return (
    <div className="flex items-center gap-2 overflow-x-auto">
      <Select value={diet} onValueChange={setDiet}><SelectTrigger className="w-[180px] bg-white"><SelectValue placeholder="Diet" /></SelectTrigger><SelectContent><SelectItem value="all">All diets</SelectItem>{DIETS.map(d => (<SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>))}</SelectContent></Select>
      <Select value={sort} onValueChange={setSort}><SelectTrigger className="w-[180px] bg-white"><SelectValue placeholder="Sort by" /></SelectTrigger><SelectContent><SelectItem value="rating">Top rated</SelectItem><SelectItem value="fastest">Fastest</SelectItem><SelectItem value="reviews">Most reviewed</SelectItem></SelectContent></Select>
      <div className="ml-auto hidden md:flex items-center gap-3 text-xs text-neutral-600"><Bike size={14} /> Weekly delivery<span className="text-neutral-300">|</span><Timer size={14} /> Pickup available</div>
    </div>
  );
}

// ---------------------- Customization Dialog ----------------------
export function CustomizeAndAdd({ item, vendor }) {
  const { toast } = useToast();
  const { addItem, sub } = useCart();
  const [selected, setSelected] = useState([]);
  const [subscriptionId, setSubscriptionId] = useState("sub-5");
  const totalExtras = selected.reduce((acc, id) => acc + (CUSTOMIZATION_OPTIONS.find(o=>o.id===id)?.fee||0), 0);
  const chosenSub = SUBSCRIPTIONS.find(s => s.id === subscriptionId) || sub;
  const discountFactor = chosenSub.priceMultiplier / chosenSub.meals;
  const totalPrice = Math.round((item.price * discountFactor + totalExtras) * chosenSub.meals * 100) / 100;
  function toggle(id){ setSelected(prev=> prev.includes(id)? prev.filter(x=>x!==id): [...prev, id]); }
  function onAdd(){ addItem({ vendorId: vendor.id, vendorName: vendor.name, itemId: item.id, name: item.name, subscriptionId: chosenSub.id, meals: chosenSub.meals, customizations: selected, unitPrice: Math.round((item.price*discountFactor+totalExtras)*100)/100, totalPrice, image: item.image }); toast({ title: "Added to cart", description: `${item.name} • ${chosenSub.label}` }); }
  return (<Dialog><DialogTrigger asChild><Button variant="outline" className="w-full">Customize &amp; Add</Button></DialogTrigger><DialogContent className="max-w-xl"><DialogHeader><DialogTitle>{item.name}</DialogTitle></DialogHeader><div className="flex gap-4"><img loading="lazy" src={item.image} alt={item.name} className="w-40 h-32 object-cover rounded-md border" /><div className="flex-1"><p className="text-sm text-neutral-600 mb-3">{item.desc}</p><div className="mb-3"><span className="text-sm font-medium">Subscription</span><Select value={subscriptionId} onValueChange={setSubscriptionId}><SelectTrigger className="mt-1 bg-white"><SelectValue /></SelectTrigger><SelectContent>{SUBSCRIPTIONS.map(s => (<SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>))}</SelectContent></Select></div><div className="grid grid-cols-2 gap-2">{CUSTOMIZATION_OPTIONS.map(opt => (<label key={opt.id} className="flex items-center gap-2 text-sm border rounded-md p-2 bg-white cursor-pointer"><Checkbox checked={selected.includes(opt.id)} onCheckedChange={()=>toggle(opt.id)} /><span>{opt.label}</span></label>))}</div><div className="mt-4 flex items-center justify-between"><div className="text-sm text-neutral-600">Per week total</div><div className="text-lg font-semibold">${totalPrice.toFixed(2)}</div></div><Button onClick={onAdd} className="mt-3 w-full bg-[#2a7d4f] text-white hover:bg-[#236840]">Add to cart</Button></div></div></DialogContent></Dialog>);
}

// ---------------------- Cart ----------------------
export function CartContents({ compact = false }) {
  const { items, removeItem, total, subscriptionId, setSubscriptionId, fulfillment, setFulfillment } = useCart();
  const navigate = useNavigate();
  return (
    <div className="flex h-full flex-col">
      <div className="mt-4 space-y-3 flex-1 overflow-auto pr-1 scroll-touch">
        <div className="flex items-center gap-2"><span className="text-sm">Plan:</span><Select value={subscriptionId} onValueChange={setSubscriptionId}><SelectTrigger className="bg-white w-[180px]"><SelectValue /></SelectTrigger><SelectContent>{SUBSCRIPTIONS.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent></Select><span className="ml-3 text-sm">Fulfillment:</span><Select value={fulfillment} onValueChange={setFulfillment}><SelectTrigger className="bg-white w-[160px]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="weekly">Weekly delivery</SelectItem><SelectItem value="pickup">Pickup</SelectItem></SelectContent></Select></div>
        {items.length===0 && (<div className="text-sm text-neutral-500 mt-6">Your cart is empty.</div>)}
        {items.map((it, idx) => (<div key={idx} className="flex gap-3 border rounded-md p-2 bg-white"><img loading="lazy" src={it.image} alt={it.name} className="w-16 h-16 object-cover rounded" /><div className="flex-1 min-w-0"><div className="flex items-start justify-between"><div><div className="font-medium text-sm">{it.name}</div><div className="text-xs text-neutral-500">{it.vendorName} • {it.meals} meals/week</div></div><button onClick={()=>removeItem(idx)} className="text-neutral-400 hover:text-red-600"><Trash2 size={16} /></button></div>{it.customizations?.length>0 && (<div className="mt-1 text-xs text-neutral-500 truncate">{it.customizations.map(id => CUSTOMIZATION_OPTIONS.find(o => o.id === id)?.label).join(", ")}</div>)}<div className="mt-1 text-sm font-semibold">${it.totalPrice.toFixed(2)}</div></div></div>))}
      </div>
      <div className="border-t pt-3"><div className="flex items-center justify-between text-sm mb-2"><span>Subtotal</span><span className="font-semibold">${total.toFixed(2)}</span></div><Button disabled={items.length===0} onClick={()=>navigate("/checkout")} className="w-full bg-[#2a7d4f] text-white hover:bg-[#236840]">Checkout</Button></div>
    </div>
  );
}

export function Providers({ children }) { return (<AuthProvider><CartProvider>{children}</CartProvider></AuthProvider>); }